import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import { useChat } from './useChat';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ── Helpers ───────────────────────────────────────────────────────────────────
const safeUser = () => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } };
const token    = () => localStorage.getItem('user_token') || '';
const authHdr  = () => ({ Authorization: `Token ${token()}`, 'Content-Type': 'application/json' });

const roleIcon  = r => ({ dispatcher:'🎧', company:'🏢', driver:'🚛' }[r] || '👤');
const roleLabel = r => ({ dispatcher:'Dispatcher', company:'Company', driver:'Driver' }[r] || 'User');
const initials  = name => {
  if (!name) return 'U';
  const p = name.trim().split(' ').filter(Boolean);
  return p.length > 1 ? (p[0][0]+p[p.length-1][0]).toUpperCase() : name.slice(0,2).toUpperCase();
};
const fmtTime = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
};
const fmtConvoTime = iso => {
  if (!iso) return '';
  const d    = new Date(iso);
  const diff = Math.floor((Date.now() - d) / 86400000);
  if (diff === 0) return d.toLocaleTimeString([],{hour:'numeric',minute:'2-digit'});
  if (diff === 1) return 'Yesterday';
  if (diff <  7)  return d.toLocaleDateString([],{weekday:'short'});
  return d.toLocaleDateString([],{month:'short',day:'numeric'});
};

// ── Tick ──────────────────────────────────────────────────────────────────────
function Ticks({ status }) {
  const c = status === 'read' ? '#3BA3FF' : '#9ca3af';
  const p = (
    <svg width={12} height={12} viewBox="0 0 14 14" fill="none">
      <polyline points="2,8 5.5,11 12,3.5" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  return status === 'sent'
    ? <span style={{display:'inline-flex'}}>{p}</span>
    : <span style={{display:'inline-flex'}}>{p}<span style={{marginLeft:-6}}>{p}</span></span>;
}

// ── GPS Map bubble ────────────────────────────────────────────────────────────
function GPSBubble({ lat, lng, address, D }) {
  const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  return (
    <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
      style={{ display:'block', textDecoration:'none', borderRadius:12, overflow:'hidden',
        border:`1px solid ${D?'rgba(255,255,255,0.15)':'#e5e7eb'}`,
        maxWidth:240, boxShadow:'0 2px 8px rgba(0,0,0,0.15)' }}>
      <img
        src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=300x160&markers=${lat},${lng}&key=${process.env.REACT_APP_MAPS_KEY||''}`}
        alt="Map"
        onError={e => { e.target.src = `https://via.placeholder.com/300x160/1e293b/94a3b8?text=📍+${lat?.toFixed(3)},${lng?.toFixed(3)}`; }}
        style={{ width:'100%', display:'block', height:120, objectFit:'cover' }}
      />
      <div style={{ padding:'8px 10px', background:D?'#1e293b':'#f8fafc' }}>
        <div style={{ fontSize:12, fontWeight:700, color:'#10b981' }}>📍 Live Location</div>
        {address && <div style={{ fontSize:11, color:D?'#94a3b8':'#6b7280', marginTop:2 }}>{address}</div>}
        <div style={{ fontSize:11, color:'#3b82f6', marginTop:3 }}>Tap to open in Maps →</div>
      </div>
    </a>
  );
}

// ── Message Bubble ────────────────────────────────────────────────────────────
function MsgBubble({ msg, isMe, D }) {
  const time    = fmtTime(msg.created_at);
  const mtype   = msg.message_type || 'text';
  const content = typeof msg.content === 'object' ? JSON.stringify(msg.content) : (msg.content || '');

  const bubbleBg  = isMe ? (D?'#1d4ed8':'#0f172a') : (D?'#1e293b':'#fff');
  const bubbleClr = isMe ? '#fff' : (D?'#f0f4f8':'#111827');
  const bubbleBdr = isMe ? 'none' : `1px solid ${D?'rgba(255,255,255,0.10)':'#e5e7eb'}`;
  const radius    = isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px';

  return (
    <div style={{ display:'flex', justifyContent:isMe?'flex-end':'flex-start',
      marginBottom:6, paddingLeft:isMe?56:0, paddingRight:isMe?0:56 }}>
      <div style={{ maxWidth:'72%', minWidth:0 }}>
        {/* Sender name for group context */}
        {!isMe && msg.sender_name && (
          <div style={{ fontSize:11, fontWeight:700, color:'#0891b2', marginBottom:2, paddingLeft:4 }}>
            {msg.sender_name}
          </div>
        )}
        <div style={{ background:bubbleBg, color:bubbleClr, borderRadius:radius,
          border:bubbleBdr, padding:'8px 11px 5px', boxShadow:'0 1px 2px rgba(0,0,0,0.08)',
          wordBreak:'break-word' }}>

          {/* GPS request */}
          {mtype === 'gps_request' && (
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:18 }}>📍</span>
              <span style={{ fontSize:13, fontWeight:600 }}>{content || 'Location requested'}</span>
            </div>
          )}

          {/* GPS update — map */}
          {mtype === 'gps_update' && msg.gps_lat && msg.gps_lng && (
            <GPSBubble lat={msg.gps_lat} lng={msg.gps_lng} address={msg.gps_address} D={D}/>
          )}
          {mtype === 'gps_update' && !msg.gps_lat && (
            <div style={{ fontSize:13 }}>📍 {content}</div>
          )}

          {/* Image */}
          {mtype === 'image' && (msg.image_url || msg.image_data) && (
            <div>
              <img
                src={msg.image_url || `data:image/jpeg;base64,${msg.image_data}`}
                alt="Sent image"
                onClick={() => window.open(msg.image_url || `data:image/jpeg;base64,${msg.image_data}`, '_blank')}
                style={{ maxWidth:220, maxHeight:200, borderRadius:10, display:'block',
                  cursor:'pointer', objectFit:'cover' }}
              />
              {content && <div style={{ fontSize:13, marginTop:5 }}>{content}</div>}
            </div>
          )}
          {mtype === 'image' && !msg.image_url && !msg.image_data && (
            <div style={{ fontSize:13, color:'#9ca3af' }}>🖼️ Image loading…</div>
          )}

          {/* Text */}
          {(mtype === 'text' || mtype === 'offer') && (
            <p style={{ margin:0, fontSize:14, lineHeight:1.55 }}>{content}</p>
          )}

          {/* Time + ticks */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end',
            gap:3, marginTop:4 }}>
            <span style={{ fontSize:10, color:isMe?'rgba(255,255,255,0.45)':'#9ca3af' }}>{time}</span>
            {isMe && <Ticks status={msg.status || 'read'}/>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── GPS Request Banner (shown to driver) ──────────────────────────────────────
function GPSRequestBanner({ session, onShare, onDecline, D }) {
  if (!session || session.status !== 'requested') return null;
  return (
    <div style={{ background:'#fef3c7', border:'1.5px solid #f59e0b',
      borderRadius:12, padding:'12px 16px', margin:'8px 12px',
      display:'flex', gap:12, alignItems:'center' }}>
      <span style={{ fontSize:24 }}>📍</span>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, fontSize:13, color:'#92400e' }}>Dispatcher requested your location</div>
        <div style={{ fontSize:12, color:'#b45309', marginTop:2 }}>Share your GPS to assist with dispatch coordination</div>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={onShare}
          style={{ padding:'7px 14px', borderRadius:99, border:'none',
            background:'#f59e0b', color:'#0d1f3c', fontWeight:800, fontSize:12, cursor:'pointer' }}>
          Share
        </button>
        <button onClick={onDecline}
          style={{ padding:'7px 12px', borderRadius:99, border:'1px solid #f59e0b',
            background:'transparent', color:'#92400e', fontSize:12, cursor:'pointer' }}>
          Decline
        </button>
      </div>
    </div>
  );
}

// ── Convo Row ─────────────────────────────────────────────────────────────────
function ConvoRow({ c, active, onClick, D }) {
  const name    = c.partner_name || `${c.partner_first_name||''} ${c.partner_last_name||''}`.trim() || c.partner_email || 'User';
  const unread  = c.unread_count || 0;
  const preview = c.last_message_text || '—';
  const role    = c.partner_role || 'driver';

  const bgA   = D?'rgba(59,130,246,0.18)':'#dbeafe';
  const bdrA  = '3px solid #3b82f6';
  const hov   = D?'rgba(255,255,255,0.05)':'#f7f8fa';
  const txtC  = D?'#f0f4f8':'#111827';
  const subC  = D?'#94a3b8':'#6b7280';
  const bdrC  = D?'#1e293b':'#f1f5f9';

  return (
    <div onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', cursor:'pointer',
        background:active?bgA:'transparent',
        borderBottom:`1px solid ${bdrC}`,
        borderLeft: active?bdrA:'3px solid transparent',
        transition:'background .12s' }}
      onMouseEnter={e=>{ if(!active) e.currentTarget.style.background=hov; }}
      onMouseLeave={e=>{ e.currentTarget.style.background = active?bgA:'transparent'; }}>

      {/* Avatar */}
      <div style={{ position:'relative', flexShrink:0 }}>
        <div style={{ width:44, height:44, borderRadius:'50%',
          background:'linear-gradient(135deg,#0891b2,#1d4ed8)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontWeight:800, fontSize:15, color:'#fff' }}>
          {initials(name)}
        </div>
        <span style={{ position:'absolute', bottom:0, right:0, fontSize:13,
          background:'white', borderRadius:'50%', lineHeight:1 }}>
          {roleIcon(role)}
        </span>
        {unread > 0 && (
          <span style={{ position:'absolute', top:-2, right:-2, minWidth:18, height:18,
            borderRadius:99, background:'#ef4444', border:'2px solid white',
            fontSize:10, fontWeight:800, color:'#fff',
            display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </div>

      {/* Text */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
          <span style={{ fontWeight:700, fontSize:13.5, color:txtC,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120 }}>
            {name}
          </span>
          <span style={{ fontSize:10, color:subC, whiteSpace:'nowrap', marginLeft:4 }}>
            {fmtConvoTime(c.updated_at)}
          </span>
        </div>
        <div style={{ fontSize:12, color:unread>0?txtC:subC, fontWeight:unread>0?600:400,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {preview.length > 40 ? preview.slice(0,40)+'…' : preview}
        </div>
        {/* Listing badge (TradeLink) */}
        {c.listing_title && (
          <div style={{ fontSize:10, color:'#0891b2', marginTop:1,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            🏷️ {c.listing_title}
            {c.listing_price && ` · $${c.listing_price}`}
          </div>
        )}
        <div style={{ fontSize:10, color:subC, marginTop:1 }}>
          {roleIcon(role)} {roleLabel(role)}
        </div>
      </div>
    </div>
  );
}

// ── Image picker ──────────────────────────────────────────────────────────────
function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload  = () => res(r.result);
    r.onerror = () => rej(new Error('Read failed'));
    r.readAsDataURL(file);
  });
}

// ── Chat Panel ────────────────────────────────────────────────────────────────
function ChatPanel({ activeConvo, convoType, currentUser, isDark, onGPSRequest }) {
  const D       = isDark;
  const [input, setInput]     = useState('');
  const [imgPreview, setImgPreview] = useState(null); // {data, name, file}
  const [caption,    setCaption]    = useState('');
  const [tracking,   setTracking]   = useState(false);
  const gpsWatchRef   = useRef(null);
  const messagesEndRef = useRef(null);
  const fileRef        = useRef(null);

  const { messages, isConnected, peerPresence, gpsSession, setGpsSession,
          sendText, sendImage, requestGPS, sendGPSUpdate } = useChat({
    type: convoType, conversationId: activeConvo?.id, currentUser,
  });

  const myId   = currentUser?.id || currentUser?.pk;
  const myRole = currentUser?.role || currentUser?.profile?.role || 'driver';
  const isDisp = myRole === 'dispatcher';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  // GPS share handler (driver taps Share)
  const handleShareGPS = useCallback(() => {
    if (!navigator.geolocation) { alert('Geolocation not supported'); return; }
    setTracking(true);
    gpsWatchRef.current = navigator.geolocation.watchPosition(
      pos => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        sendGPSUpdate(lat, lng, accuracy, '', gpsSession?.sessionId);
        setGpsSession(prev => prev ? { ...prev, lat, lng, status:'active' } : null);
      },
      () => { setTracking(false); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
  }, [gpsSession, sendGPSUpdate, setGpsSession]);

  const handleStopGPS = useCallback(() => {
    if (gpsWatchRef.current) navigator.geolocation.clearWatch(gpsWatchRef.current);
    setTracking(false);
    setGpsSession(null);
  }, [setGpsSession]);

  const handleSend = useCallback(async (e) => {
    e?.preventDefault();
    if (imgPreview) {
      const ok = sendImage(imgPreview.data.split(',')[1], imgPreview.name, caption);
      if (ok) { setImgPreview(null); setCaption(''); }
      return;
    }
    if (!input.trim()) return;
    sendText(input.trim());
    setInput('');
  }, [input, imgPreview, caption, sendText, sendImage]);

  const handleFile = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('Max image size 10MB'); return; }
    const data = await fileToBase64(file);
    setImgPreview({ data, name: file.name, file });
    e.target.value = '';
  }, []);

  if (!activeConvo) return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', gap:10, background:D?'#0f172a':'#efeae2' }}>
      <div style={{ fontSize:52 }}>💬</div>
      <div style={{ fontWeight:700, fontSize:17, color:D?'#64748b':'#94a3b8' }}>Select a conversation</div>
    </div>
  );

  const partnerName = activeConvo.partner_name ||
    `${activeConvo.partner_first_name||''} ${activeConvo.partner_last_name||''}`.trim() ||
    'User';

  const presenceText = peerPresence.status === 'online' ? 'Online' :
    peerPresence.lastSeen ? `Last seen ${fmtTime(peerPresence.lastSeen)}` : 'Offline';
  const presenceColor = peerPresence.status === 'online' ? '#22c55e' : '#9ca3af';

  const hdrBg  = D?'#1e293b':'#0f172a';
  const inpBg  = D?'#1e293b':'#fff';
  const inpBdr = D?'#334155':'#e5e7eb';
  const msgsBg = D?'#0f172a':'#efeae2';

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:msgsBg }}>

      {/* Header */}
      <div style={{ background:hdrBg, padding:'10px 14px', display:'flex',
        alignItems:'center', gap:10, flexShrink:0, boxShadow:'0 2px 6px rgba(0,0,0,0.2)' }}>
        <div style={{ width:40, height:40, borderRadius:'50%',
          background:'linear-gradient(135deg,#0891b2,#1d4ed8)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontWeight:800, fontSize:14, color:'#fff', flexShrink:0 }}>
          {initials(partnerName)}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15, color:'#fff',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {roleIcon(activeConvo.partner_role)} {partnerName}
          </div>
          <div style={{ fontSize:11, color:presenceColor, marginTop:1 }}>
            {presenceText}
            {activeConvo.listing_title && (
              <span style={{ color:'rgba(255,255,255,0.45)', marginLeft:8 }}>
                · 🏷️ {activeConvo.listing_title}
              </span>
            )}
          </div>
        </div>

        {/* Dispatcher GPS button */}
        {isDisp && (
          <button onClick={requestGPS}
            title="Request driver location"
            style={{ padding:'6px 12px', borderRadius:99, border:'1.5px solid #f59e0b',
              background:'rgba(245,158,11,0.12)', color:'#f59e0b',
              fontSize:12, fontWeight:700, cursor:'pointer' }}>
            📍 GPS
          </button>
        )}

        {/* Live indicator */}
        <div style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:99,
          background:isConnected?'rgba(34,197,94,0.15)':'rgba(156,163,175,0.12)',
          color:isConnected?'#22c55e':'#9ca3af',
          border:`1px solid ${isConnected?'rgba(34,197,94,0.35)':'rgba(156,163,175,0.30)'}` }}>
          {isConnected ? '● Live' : '○ Off'}
        </div>
      </div>

      {/* Listing card (TradeLink) */}
      {activeConvo.listing_id && activeConvo.listing_title && (
        <div style={{ background:D?'rgba(8,145,178,0.10)':'#f0f9ff',
          border:`1px solid ${D?'rgba(8,145,178,0.20)':'#bae6fd'}`,
          padding:'8px 14px', flexShrink:0, display:'flex', alignItems:'center', gap:10 }}>
          {activeConvo.listing_image && (
            <img src={activeConvo.listing_image} alt="Item"
              style={{ width:40, height:40, borderRadius:8, objectFit:'cover', flexShrink:0 }}/>
          )}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700, color:D?'#38bdf8':'#0369a1',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {activeConvo.listing_title}
            </div>
            {activeConvo.listing_price && (
              <div style={{ fontSize:12, color:'#c9971c', fontWeight:800 }}>
                ${activeConvo.listing_price}
              </div>
            )}
          </div>
        </div>
      )}

      {/* GPS share banner for driver */}
      {gpsSession?.status === 'requested' && myRole === 'driver' && (
        <GPSRequestBanner session={gpsSession} onShare={handleShareGPS}
          onDecline={()=>setGpsSession(null)} D={D}/>
      )}

      {/* Active GPS tracking bar */}
      {tracking && (
        <div style={{ background:'#dcfce7', border:'1px solid #86efac',
          padding:'8px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#166534' }}>
            📍 Sharing live location…
          </div>
          <button onClick={handleStopGPS}
            style={{ padding:'4px 12px', borderRadius:99, border:'1px solid #16a34a',
              background:'transparent', color:'#16a34a', fontSize:12, cursor:'pointer', fontWeight:700 }}>
            Stop
          </button>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:'12px 10px', display:'flex', flexDirection:'column', gap:2 }}>
        {messages.length === 0 && (
          <div style={{ textAlign:'center', color:'#9ca3af', fontSize:13, marginTop:48 }}>
            No messages yet — say hello! 👋
          </div>
        )}
        {messages.map((msg, i) => (
          <MsgBubble key={msg.id || `t${i}`}
            msg={msg} isMe={msg.sender === myId} D={D}/>
        ))}
        <div ref={messagesEndRef}/>
      </div>

      {/* Image preview bar */}
      {imgPreview && (
        <div style={{ background:D?'#1e293b':'#f8fafc', borderTop:`1px solid ${inpBdr}`,
          padding:'10px 12px', display:'flex', gap:10, alignItems:'flex-start', flexShrink:0 }}>
          <img src={imgPreview.data} alt="preview"
            style={{ width:56, height:56, objectFit:'cover', borderRadius:8, flexShrink:0 }}/>
          <div style={{ flex:1, minWidth:0 }}>
            <input value={caption} onChange={e=>setCaption(e.target.value)}
              placeholder="Add a caption (optional)…"
              style={{ width:'100%', background:'transparent', border:'none', outline:'none',
                fontSize:13, color:D?'#f0f4f8':'#111827', boxSizing:'border-box' }}/>
            <div style={{ fontSize:11, color:'#9ca3af', marginTop:3 }}>{imgPreview.name}</div>
          </div>
          <button onClick={()=>setImgPreview(null)}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#ef4444', padding:0 }}>
            ×
          </button>
        </div>
      )}

      {/* Input bar */}
      <form onSubmit={handleSend}
        style={{ background:inpBg, borderTop:`1px solid ${inpBdr}`,
          padding:'9px 10px', display:'flex', gap:8, alignItems:'flex-end', flexShrink:0 }}>

        {/* Image upload */}
        <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleFile}/>
        <button type="button" onClick={()=>fileRef.current?.click()}
          title="Send image"
          style={{ width:38, height:38, borderRadius:'50%', border:`1px solid ${inpBdr}`,
            background:D?'rgba(255,255,255,0.06)':'#f8fafc', color:'#0891b2',
            cursor:'pointer', fontSize:18, flexShrink:0, display:'flex',
            alignItems:'center', justifyContent:'center' }}>
          📷
        </button>

        {/* Text input */}
        <input value={input} onChange={e=>setInput(e.target.value)}
          placeholder={imgPreview ? 'Press send to send image…' : 'Type a message…'}
          disabled={!!imgPreview}
          onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); handleSend(); } }}
          style={{ flex:1, minWidth:0, background:D?'rgba(255,255,255,0.06)':'#f7f8fa',
            border:`1.5px solid ${inpBdr}`, borderRadius:22,
            padding:'9px 14px', color:D?'#f0f4f8':'#111827',
            fontSize:14, outline:'none', transition:'border-color .15s' }}
          onFocus={e=>e.target.style.borderColor='#0891b2'}
          onBlur={e=>e.target.style.borderColor=inpBdr}
        />

        {/* Send */}
        <button type="submit"
          disabled={!input.trim() && !imgPreview}
          style={{ width:40, height:40, borderRadius:'50%', border:'none',
            background:(input.trim()||imgPreview)?'#1d4ed8':'#e5e7eb',
            color:'#fff', cursor:(input.trim()||imgPreview)?'pointer':'not-allowed',
            fontSize:17, flexShrink:0, display:'flex',
            alignItems:'center', justifyContent:'center', transition:'background .15s' }}>
          ➤
        </button>
      </form>
    </div>
  );
}

// ── New DM Modal ──────────────────────────────────────────────────────────────
function NewDMModal({ onStart, onClose, D }) {
  const [q,       setQ]       = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    fetch(`${API}/api/users/search/?q=${encodeURIComponent(q)}`, { headers: authHdr() })
      .then(r=>r.json()).then(data=>{ setResults(Array.isArray(data)?data:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  }, [q]);

  const overlay = { position:'fixed', inset:0, zIndex:900, background:'rgba(0,0,0,0.55)',
    display:'flex', alignItems:'center', justifyContent:'center', padding:20 };
  const box = { width:'100%', maxWidth:420, background:D?'#1e293b':'#fff',
    borderRadius:18, padding:24, boxShadow:'0 20px 60px rgba(0,0,0,0.4)',
    border:`1px solid ${D?'#334155':'#e5e7eb'}` };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontWeight:800, fontSize:17, color:D?'#f0f4f8':'#111827' }}>New Message</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
            fontSize:20, color:'#9ca3af' }}>×</button>
        </div>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search drivers, companies, dispatchers…"
          autoFocus
          style={{ width:'100%', padding:'10px 14px', borderRadius:10, boxSizing:'border-box',
            border:`1.5px solid ${D?'#334155':'#e5e7eb'}`, background:D?'#0f172a':'#f8fafc',
            color:D?'#f0f4f8':'#111827', fontSize:14, outline:'none' }}/>
        <div style={{ marginTop:12, maxHeight:260, overflowY:'auto' }}>
          {loading && <div style={{ textAlign:'center', color:'#9ca3af', padding:16 }}>Searching…</div>}
          {results.map(u => (
            <div key={u.id} onClick={()=>onStart(u)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 4px',
                cursor:'pointer', borderBottom:`1px solid ${D?'#1e293b':'#f1f5f9'}` }}
              onMouseEnter={e=>e.currentTarget.style.background=D?'rgba(255,255,255,0.05)':'#f8fafc'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{ width:38, height:38, borderRadius:'50%', background:'linear-gradient(135deg,#0891b2,#1d4ed8)',
                display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, color:'#fff' }}>
                {initials(u.name)}
              </div>
              <div>
                <div style={{ fontWeight:600, fontSize:13.5, color:D?'#f0f4f8':'#111827' }}>
                  {roleIcon(u.role)} {u.name}
                </div>
                <div style={{ fontSize:11, color:'#9ca3af' }}>{roleLabel(u.role)} · {u.email}</div>
              </div>
            </div>
          ))}
          {!loading && q.length >= 2 && results.length === 0 && (
            <div style={{ textAlign:'center', color:'#9ca3af', padding:16, fontSize:13 }}>No users found</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main UniversalChat page ───────────────────────────────────────────────────
export default function UniversalChat({ isDarkMode, toggleTheme }) {
  const D = !!isDarkMode;
  const navigate = useNavigate();
  const currentUser = safeUser();

  const [tab,          setTab]          = useState('tradelink'); // 'tradelink' | 'direct'
  const [tlConvos,     setTlConvos]     = useState([]);
  const [dmConvos,     setDmConvos]     = useState([]);
  const [activeConvo,  setActiveConvo]  = useState(null);
  const [activeType,   setActiveType]   = useState('tradelink');
  const [showNewDM,    setShowNewDM]    = useState(false);
  const [narrow,       setNarrow]       = useState(window.innerWidth < 680);
  const [loadingConvos,setLoadingConvos]= useState(false);

  useEffect(() => {
    const fn = () => setNarrow(window.innerWidth < 680);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const fetchConvos = useCallback(async () => {
    if (!token()) return;
    setLoadingConvos(true);
    try {
      const [tlRes, dmRes] = await Promise.all([
        fetch(`${API}/api/chat/tradelink/inbox/`, { headers: authHdr() }),
        fetch(`${API}/api/chat/direct/inbox/`,    { headers: authHdr() }),
      ]);
      const tl = tlRes.ok ? await tlRes.json() : [];
      const dm = dmRes.ok ? await dmRes.json() : [];
      setTlConvos(Array.isArray(tl) ? tl : []);
      setDmConvos(Array.isArray(dm) ? dm : []);
    } finally {
      setLoadingConvos(false);
    }
  }, []);

  useEffect(() => { fetchConvos(); }, [fetchConvos]);

  const handleStartDM = useCallback(async (targetUser) => {
    setShowNewDM(false);
    const res  = await fetch(`${API}/api/chat/direct/start/`, {
      method:'POST', headers: authHdr(),
      body: JSON.stringify({ user_id: targetUser.id }),
    });
    const data = await res.json();
    if (data.conversation_id) {
      const convo = {
        id: data.conversation_id,
        partner_id:         targetUser.id,
        partner_name:       targetUser.name,
        partner_first_name: targetUser.first_name,
        partner_last_name:  targetUser.last_name,
        partner_email:      targetUser.email,
        partner_role:       targetUser.role,
        last_message_text:  '',
        unread_count:       0,
        updated_at:         new Date().toISOString(),
      };
      setDmConvos(prev => {
        const exists = prev.find(c=>c.id===data.conversation_id);
        return exists ? prev : [convo, ...prev];
      });
      setActiveConvo(convo);
      setActiveType('direct');
      setTab('direct');
    }
  }, []);

  const convos   = tab === 'tradelink' ? tlConvos : dmConvos;
  const showList = !narrow || !activeConvo;
  const showChat = !narrow || !!activeConvo;

  // tokens
  const pageBg     = D ? '#0f172a' : '#f8fafc';
  const sidebarBg  = D ? '#1e293b' : '#ffffff';
  const bdrC       = D ? '#1e293b' : '#e5e7eb';
  const tabActiveBg= D ? 'rgba(59,130,246,0.20)' : '#dbeafe';
  const tabC       = D ? '#94a3b8' : '#6b7280';
  const txtC       = D ? '#f0f4f8' : '#111827';
  const mutedC     = D ? '#64748b' : '#9ca3af';

  return (
    <div style={{ minHeight:'100vh', background:pageBg, display:'flex', flexDirection:'column',
      fontFamily:"'Inter',system-ui,sans-serif" }}>
      <Navbar isDarkMode={D} toggleTheme={toggleTheme}/>

      <div style={{ display:'flex', height:'calc(100vh - 62px)', overflow:'hidden' }}>

        {/* ── Sidebar ── */}
        {showList && (
          <div style={{ width:narrow?'100%':320, flexShrink:0, display:'flex',
            flexDirection:'column', height:'100%', background:sidebarBg,
            borderRight:narrow?'none':`1px solid ${bdrC}` }}>

            {/* Header */}
            <div style={{ padding:'14px 14px 0', flexShrink:0 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <div style={{ fontWeight:900, fontSize:18, color:txtC }}>💬 Messages</div>
                <button onClick={()=>setShowNewDM(true)}
                  style={{ width:34, height:34, borderRadius:'50%', border:'none',
                    background:'#1d4ed8', color:'#fff', fontSize:20,
                    cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  +
                </button>
              </div>

              {/* Tabs */}
              <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                {['tradelink','direct'].map(t => (
                  <button key={t} onClick={()=>{ setTab(t); setActiveConvo(null); }}
                    style={{ flex:1, padding:'7px 0', borderRadius:9, border:'none', cursor:'pointer',
                      fontWeight:700, fontSize:12,
                      background:tab===t ? tabActiveBg : 'transparent',
                      color:tab===t ? '#3b82f6' : tabC,
                      transition:'all .15s' }}>
                    {t==='tradelink' ? '🛒 TradeLink' : '💬 Direct'}
                    {t==='direct' && dmConvos.some(c=>c.unread_count>0) && (
                      <span style={{ marginLeft:5, background:'#ef4444', color:'#fff',
                        borderRadius:99, fontSize:9, padding:'1px 5px', fontWeight:900 }}>
                        {dmConvos.reduce((a,c)=>a+(c.unread_count||0),0)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* List */}
            <div style={{ flex:1, overflowY:'auto' }}>
              {loadingConvos && (
                <div style={{ padding:24, textAlign:'center', color:mutedC, fontSize:13 }}>Loading…</div>
              )}
              {!loadingConvos && convos.length === 0 && (
                <div style={{ padding:32, textAlign:'center', color:mutedC, fontSize:13 }}>
                  {tab==='tradelink' ? 'No TradeLink chats yet.\nBrowse the marketplace to start buying!' : 'No direct messages yet.\nTap + to message someone.'}
                </div>
              )}
              {convos.map(c => (
                <ConvoRow key={c.id} c={c} active={activeConvo?.id===c.id && activeType===tab}
                  onClick={()=>{ setActiveConvo(c); setActiveType(tab); }} D={D}/>
              ))}
            </div>
          </div>
        )}

        {/* ── Chat Panel ── */}
        {showChat && (
          <div style={{ flex:1, height:'100%', minWidth:0, overflow:'hidden', display:'flex', flexDirection:'column' }}>
            {narrow && activeConvo && (
              <button onClick={()=>setActiveConvo(null)}
                style={{ padding:'10px 14px', background:D?'#1e293b':'#f8fafc',
                  border:'none', borderBottom:`1px solid ${bdrC}`, cursor:'pointer',
                  textAlign:'left', fontSize:13, color:'#3b82f6', fontWeight:700 }}>
                ← Back
              </button>
            )}
            <ChatPanel activeConvo={activeConvo} convoType={activeType}
              currentUser={currentUser} isDark={D}/>
          </div>
        )}
      </div>

      {showNewDM && (
        <NewDMModal onStart={handleStartDM} onClose={()=>setShowNewDM(false)} D={D}/>
      )}
    </div>
  );
}
